/**
 * Redimensiona mantendo o aspecto original (SEM quadrar), reduzindo só se o maior lado
 * passar de maxLado (nunca amplia). Saída JPEG com fundo branco — segura para o backend
 * ler e converter em WebP. Usada na Galeria (upload de fotos avulsas).
 */
export const redimensionarMantendoAspecto = (
    file: File,
    maxLado = 1280,
    quality = 0.85
) => {
    return new Promise<File>((resolve, reject) => {
        if (!file?.type?.startsWith("image/")) {
            return reject(new Error("O arquivo não é uma imagem."))
        }

        const img = new Image()
        const url = URL.createObjectURL(file)

        img.onload = () => {
            const escala = Math.min(1, maxLado / Math.max(img.width, img.height))
            const newW = Math.max(1, Math.round(img.width * escala))
            const newH = Math.max(1, Math.round(img.height * escala))

            const canvas = document.createElement("canvas")
            canvas.width = newW
            canvas.height = newH

            const ctx = canvas.getContext("2d")
            if (!ctx) {
                URL.revokeObjectURL(url)
                return reject(new Error("Falha ao obter contexto do canvas."))
            }

            ctx.imageSmoothingEnabled = true
            ctx.imageSmoothingQuality = "high"
            ctx.fillStyle = "#ffffff"
            ctx.fillRect(0, 0, newW, newH)
            ctx.drawImage(img, 0, 0, newW, newH)

            canvas.toBlob(
                blob => {
                    URL.revokeObjectURL(url)
                    if (!blob) {
                        return reject(new Error("Falha ao processar a imagem."))
                    }
                    const baseName = file.name.replace(/\.\w+$/, "")
                    const resizedFile = new File([blob], `${baseName}.jpg`, {
                        type: "image/jpeg",
                        lastModified: Date.now(),
                    })
                    resolve(resizedFile)
                },
                "image/jpeg",
                quality
            )
        }

        img.onerror = () => {
            URL.revokeObjectURL(url)
            reject(new Error("Não foi possível carregar a imagem."))
        }

        img.src = url
    })
}

export const redimensionarPara300x300 = (file: File) => {
    const size = 300
    const quality = 0.85
    const mimeType = "image/webp"
    const background = "transparent"

    return new Promise<File>((resolve, reject) => {
        if (!file?.type?.startsWith("image/")) {
            return reject(new Error("O arquivo não é uma imagem."))
        }

        const img = new Image()
        const url = URL.createObjectURL(file)

        img.onload = () => {
            const originalW = img.width
            const originalH = img.height

            const escalaOriginal = Math.min(size / originalW, size / originalH)
            const escala = Math.min(escalaOriginal, 1)

            const newW = Math.round(originalW * escala)
            const newH = Math.round(originalH * escala)

            const canvas = document.createElement("canvas")
            canvas.width = size
            canvas.height = size

            const ctx = canvas.getContext("2d")
            if (!ctx) {
                URL.revokeObjectURL(url)
                return reject(new Error("Falha ao obter contexto do canvas."))
            }

            ctx.imageSmoothingEnabled = true
            ctx.imageSmoothingQuality = "high"

            if (background !== "transparent") {
                ctx.fillStyle = background
                ctx.fillRect(0, 0, size, size)
            } else {
                ctx.clearRect(0, 0, size, size)
            }

            const dx = Math.round((size - newW) / 2)
            const dy = Math.round((size - newH) / 2)

            ctx.drawImage(img, dx, dy, newW, newH)

            canvas.toBlob(
                blob => {
                    URL.revokeObjectURL(url)

                    if (!blob) {
                        return reject(new Error("Falha ao gerar a imagem 300x300."))
                    }

                    const baseName = file.name.replace(/\.\w+$/, "")
                    const ext = "webp"

                    const resizedFile = new File(
                        [blob],
                        `${baseName}_${size}x${size}_contain.${ext}`,
                        { type: mimeType, lastModified: Date.now() }
                    )

                    resolve(resizedFile)
                },
                mimeType,
                quality
            )
        }

        img.onerror = () => {
            URL.revokeObjectURL(url)
            reject(new Error("Não foi possível carregar a imagem."))
        }

        img.src = url
    })
}
